import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { UserPlus, Settings, BarChart3, Brain, ArrowRight, Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { IN_APP_ROUTES } from '@/constants/urls';

const steps = [
  {
    number: '01',
    icon: UserPlus,
    title: 'Sign Up & Configure',
    description: 'Create your account in seconds. Add your hotel details and select your competitors to track.',
    color: 'bg-blue-500',
    videoTitle: 'Account Setup Walkthrough',
    videoDescription: 'Learn how to create your account and configure your hotel profile in under 2 minutes.',
    videoDuration: '0:48',
    videoSrc: '/videos/how-it-works/step1.mp4',
    videoSteps: [
      'Enter your email and create a password',
      'Add your hotel name and location',
      'Select up to 10 competitors to track',
      'Choose your subscription plan',
    ],
  },
  {
    number: '02',
    icon: Settings,
    title: 'Connect Your Data',
    description: 'We automatically pull rates from 15+ OTAs and track local events that impact demand.',
    color: 'bg-purple-500',
    videoTitle: 'Data Integration Guide',
    videoDescription: 'See how Prowen automatically connects to OTAs and pulls real-time rate data.',
    videoDuration: '0:50',
    videoSrc: '/videos/how-it-works/step2.mp4',
    videoSteps: [
      'Automatic OTA rate scraping from 15+ platforms',
      'Real-time price updates every 15 minutes',
      'Local events integration via Google Calendar & Ticketmaster',
      'Historical data import for trend analysis',
    ],
  },
  {
    number: '03',
    icon: BarChart3,
    title: 'Monitor & Analyze',
    description: 'View real-time rate parity, competitive positioning, and market trends in your dashboard.',
    color: 'bg-emerald-500',
    videoTitle: 'Dashboard Overview',
    videoDescription: 'Explore the powerful analytics dashboard and learn to interpret your data.',
    videoDuration: '02:02',
    videoSrc: '/videos/how-it-works/step3.mp4',
    videoSteps: [
      'Rate Parity monitoring with undercut alerts',
      'Competitive Analysis with price positioning',
      'Interactive charts and visualizations',
      'Demand Calendar with event impact indicators',
    ],
  },
  {
    number: '04',
    icon: Brain,
    title: 'Get AI Recommendations',
    description: 'Receive intelligent pricing suggestions powered by Prowen AI to maximize your revenue.',
    color: 'bg-amber-500',
    videoTitle: 'AI Insights Deep Dive',
    videoDescription: 'Discover how Prowen AI analyzes your data and provides actionable recommendations.',
    videoDuration: '0:15',
    videoSrc: '/videos/how-it-works/step4.mp4',
    videoSteps: [
      'AI-powered market analysis',
      'Dynamic pricing recommendations',
      'Revenue optimization insights',
      'PDF report generation with AI summaries',
    ],
  },
];

// Real video player component
function VideoPlayer({ step, isOpen }) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (isOpen && videoRef.current) {
      videoRef.current.play().catch(() => { });
      setIsPlaying(true);
    }
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    };
  }, [isOpen]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(progress);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleProgressClick = (e) => {
    if (videoRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = pos * videoRef.current.duration;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  return (
    <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden group">
      <video
        ref={videoRef}
        src={step.videoSrc}
        className="w-full h-full object-cover"
        muted={isMuted}
        loop
        playsInline
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
      />

      {/* Video controls overlay */}
      <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        {/* Progress bar */}
        <div
          className="w-full h-1 bg-white/30 cursor-pointer mx-4 mb-2"
          style={{ width: 'calc(100% - 32px)' }}
          onClick={handleProgressClick}
        >
          <div
            className={`h-full ${step.color} transition-all duration-100`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between px-4 pb-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 text-white hover:bg-white/20 rounded-full"
              onClick={togglePlay}
            >
              {isPlaying ? (
                <Pause className="h-5 w-5" />
              ) : (
                <Play className="h-5 w-5 ml-0.5" />
              )}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-white hover:bg-white/20 rounded-full"
              onClick={toggleMute}
            >
              {isMuted ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </Button>

            <span className="text-white text-sm">
              {formatTime(videoRef.current?.currentTime || 0)} / {formatTime(duration)}
            </span>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white hover:bg-white/20 rounded-full"
            onClick={handleFullscreen}
          >
            <Maximize className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Center play button when paused */}
      {!isPlaying && (
        <div
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          onClick={togglePlay}
        >
          <div className={`w-16 h-16 rounded-full ${step.color} flex items-center justify-center shadow-lg`}>
            <Play className="w-8 h-8 text-white ml-1" />
          </div>
        </div>
      )}
    </div>
  );
}

export default function HowItWorksSection() {
  const [selectedStep, setSelectedStep] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const navigate = useNavigate();

  const handleOpenVideo = (step) => {
    // navigate(IN_APP_ROUTES.REGISTER_PAGE)
    setSelectedStep(step)
    setIsPlaying(true)
  };

  const handleCloseVideo = () => {
    setSelectedStep(null);
    setIsPlaying(false);
  };

  return (
    <section id="how-it-works" className="py-20 lg:py-28 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 px-4 py-1.5 bg-primary/5 text-primary border-primary/20">
            Simple Setup
          </Badge>
          <p className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            How It Works
          </p>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Get started in minutes. No complex integrations or technical setup required.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Click on any step to watch the walkthrough
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              {/* Connector Line (hidden on last item and mobile) */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-12 left-[calc(50%+40px)] w-[calc(100%-80px)] h-0.5 bg-border">
                  <ArrowRight className="absolute -right-2 -top-2 w-4 h-4 text-muted-foreground" />
                </div>
              )}

              <Card
                className="h-full bg-card hover:shadow-xl transition-all duration-300 border-border/50 cursor-pointer group hover:border-primary/50 hover:-translate-y-1"
                onClick={() => handleOpenVideo(step)}
                data-testid={`how-it-works-step-${index + 1}`}
              >
                <CardContent className="p-6 text-center">
                  {/* Step Number & Icon */}
                  <div className="relative inline-flex mb-6">
                    <div className={`w-16 h-16 rounded-2xl ${step.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <step.icon className="w-8 h-8 text-white" />
                    </div>
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-card border-2 border-border flex items-center justify-center text-xs font-bold text-foreground">
                      {step.number}
                    </span>

                    {/* Play button overlay on hover */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center">
                        <Play className="w-5 h-5 text-white ml-0.5" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="font-heading text-lg font-semibold text-foreground mb-3 group-hover:text-primary transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {step.description}
                  </p>

                  {/* Watch video hint */}
                  <div className="flex items-center justify-center gap-2 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                    <Play className="w-3 h-3" />
                    <span>Watch walkthrough ({step.videoDuration})</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className="text-muted-foreground">
            Ready to get started?{' '}
            <a href="/register" className="text-primary font-medium hover:underline">
              Sign up free
            </a>
            {' '}and see results in minutes.
          </p>
        </div>
      </div>

      {/* Video Modal */}
      <Dialog open={!!selectedStep} onOpenChange={(open) => !open && handleCloseVideo()}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-card">
          {selectedStep && (
            <>
              {/* Real Video Player */}
              <VideoPlayer step={selectedStep} isOpen={!!selectedStep} />

              {/* Content */}
              {/* <div className="p-0"> */}
              {/* <DialogHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-xl ${selectedStep.color} flex items-center justify-center`}>
                      <selectedStep.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <DialogTitle className="text-xl">{selectedStep.videoTitle}</DialogTitle>
                      <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-xs mt-1">
                        AI-Generated Demo
                      </Badge>
                    </div>
                  </div>
                  <DialogDescription className="text-base">
                    {selectedStep.videoDescription}
                  </DialogDescription>
                </DialogHeader> */}

              {/* Key points */}
              {/* <div className="mt-6">
                  <h4 className="text-sm font-semibold text-foreground mb-3">What you'll learn:</h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {selectedStep.videoSteps.map((videoStep, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <div className={`w-5 h-5 rounded-full ${selectedStep.color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <span className="text-[10px] text-white font-bold">{idx + 1}</span>
                        </div>
                        {videoStep}
                      </li>
                    ))}
                  </ul>
                </div> */}

              {/* Action buttons */}
              {/* <div className="mt-6 flex gap-3">
                  <Button 
                    className="flex-1"
                    onClick={() => window.location.href = '/register'}
                  >
                    Get Started Free
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button variant="outline" onClick={handleCloseVideo}>
                    Close
                  </Button>
                </div> */}
              {/* </div> */}
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
